/**
 * Canonical Salesforce BPR Driver Map (Plan 46 G3 / Plan 47 C1)
 *
 * Single source of truth mapping prose discovery labels to actual questionnaire
 * `questionKey.fieldKey` paths, allowed types, and mapping versions.
 * Unmapped prose labels remain non-pricing discovery context.
 */
export const SALESFORCE_BPR_DRIVER_MAP = [
    // --- Lead to Opportunity ---
    {
        areaKey: 'lead_to_opportunity',
        issueKey: 'lead_routing_assignment',
        discoveryLabel: 'Lead Sources Count',
        questionKey: 'lead_to_opportunity',
        fieldKey: 'lead_sources_count',
        fullPath: 'lead_to_opportunity.lead_sources_count',
        valueType: 'number',
        description: 'Number of distinct inbound lead channels and capture forms.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'lead_to_opportunity',
        issueKey: 'lead_routing_assignment',
        discoveryLabel: 'Distinct Assignment Rules Count',
        questionKey: 'lead_to_opportunity',
        fieldKey: 'distinct_assignment_rules_count',
        fullPath: 'lead_to_opportunity.distinct_assignment_rules_count',
        valueType: 'number',
        description: 'Number of distinct territory or round-robin routing rules.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'lead_to_opportunity',
        issueKey: 'lead_routing_assignment',
        discoveryLabel: 'Custom Lead Fields in Routing',
        questionKey: 'lead_to_opportunity',
        fieldKey: 'custom_lead_fields_in_routing_count',
        fullPath: 'lead_to_opportunity.custom_lead_fields_in_routing_count',
        valueType: 'number',
        description: 'Number of custom lead qualification/routing fields evaluated during distribution.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'lead_to_opportunity',
        issueKey: 'opportunity_stage_pipeline_hygiene',
        discoveryLabel: 'Sales Process Count',
        questionKey: 'lead_to_opportunity',
        fieldKey: 'sales_process_count',
        fullPath: 'lead_to_opportunity.sales_process_count',
        valueType: 'number',
        description: 'Number of distinct sales processes / record types on Opportunity.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'lead_to_opportunity',
        issueKey: 'opportunity_stage_pipeline_hygiene',
        discoveryLabel: 'Opportunity Stage Count',
        questionKey: 'lead_to_opportunity',
        fieldKey: 'opportunity_stage_count',
        fullPath: 'lead_to_opportunity.opportunity_stage_count',
        valueType: 'number',
        description: 'Total opportunity stages across all sales processes.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'lead_to_opportunity',
        issueKey: 'activity_capture_engagement',
        discoveryLabel: 'Reps Needing Email Sync',
        questionKey: 'lead_to_opportunity',
        fieldKey: 'reps_needing_email_sync_count',
        fullPath: 'lead_to_opportunity.reps_needing_email_sync_count',
        valueType: 'number',
        description: 'Number of sales reps requiring Inbox / Einstein Activity Capture.',
        mappingVersion: '1.0',
    },
    // --- Forecasting ---
    {
        areaKey: 'forecasting',
        issueKey: 'forecast_category_accuracy',
        discoveryLabel: 'Custom Forecast Categories Count',
        questionKey: 'forecasting',
        fieldKey: 'custom_forecast_categories_count',
        fullPath: 'forecasting.custom_forecast_categories_count',
        valueType: 'number',
        description: 'Number of custom forecast categories or cumulative rollup views.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'forecasting',
        issueKey: 'multi_currency_multi_business_unit_rollup',
        discoveryLabel: 'Active Currencies Count',
        questionKey: 'forecasting',
        fieldKey: 'active_currency_count',
        fullPath: 'forecasting.active_currency_count',
        valueType: 'number',
        description: 'Number of active currencies enabled for conversion.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'forecasting',
        issueKey: 'multi_currency_multi_business_unit_rollup',
        discoveryLabel: 'Business Units Requiring Rollup',
        questionKey: 'forecasting',
        fieldKey: 'business_units_requiring_rollup_count',
        fullPath: 'forecasting.business_units_requiring_rollup_count',
        valueType: 'number',
        description: 'Number of operating business units with distinct quota rollups.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'forecasting',
        issueKey: 'quota_and_capacity_planning',
        discoveryLabel: 'Quota Rep Count',
        questionKey: 'forecasting',
        fieldKey: 'quota_rep_count',
        fullPath: 'forecasting.quota_rep_count',
        valueType: 'number',
        description: 'Number of quota-carrying reps in collaborative forecasting.',
        mappingVersion: '1.0',
    },
    // --- Quote & Pricing (Revenue Cloud / CPQ) ---
    {
        areaKey: 'quote_and_pricing',
        issueKey: 'cpq_to_rca_rebuild',
        discoveryLabel: 'CPQ Price Rules Count',
        questionKey: 'quote_and_pricing',
        fieldKey: 'cpq_price_rules_count',
        fullPath: 'quote_and_pricing.cpq_price_rules_count',
        valueType: 'number',
        description: 'Number of CPQ Price Rules being migrated or refactored.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'quote_and_pricing',
        issueKey: 'cpq_to_rca_rebuild',
        discoveryLabel: 'Quote Calculator Plugins Count',
        questionKey: 'quote_and_pricing',
        fieldKey: 'quote_calculator_plugins_count',
        fullPath: 'quote_and_pricing.quote_calculator_plugins_count',
        valueType: 'number',
        description: 'Number of custom JS Quote Calculator Plugins (QCP).',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'quote_and_pricing',
        issueKey: 'cpq_to_rca_rebuild',
        discoveryLabel: 'Reviewed QCP Rebuild Hours',
        questionKey: 'quote_and_pricing',
        fieldKey: 'reviewed_qcp_rebuild_hours',
        fullPath: 'quote_and_pricing.reviewed_qcp_rebuild_hours',
        valueType: 'number',
        description: 'Explicit reviewer-confirmed build and test hours to rebuild or retain Quote Calculator Plugin (QCP) behavior in RCA. Only extract this when the transcript states an actual reviewed hours figure for QCP rebuild work - do not infer it from the raw plugin count.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'quote_and_pricing',
        issueKey: 'cpq_to_rca_rebuild',
        discoveryLabel: 'Reviewed QCP Work Packages Count',
        questionKey: 'quote_and_pricing',
        fieldKey: 'reviewed_qcp_work_packages_count',
        fullPath: 'quote_and_pricing.reviewed_qcp_work_packages_count',
        valueType: 'number',
        description: 'Number of reviewed and approved Quote Calculator Plugin (QCP) work packages, each billed at a standard per-package hour allowance. Only extract this when the transcript states a reviewed/approved work-package count for QCP rebuild, not the raw plugin inventory count.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'quote_and_pricing',
        issueKey: 'cpq_to_rca_rebuild',
        discoveryLabel: 'CPQ Approval Chains Count',
        questionKey: 'quote_and_pricing',
        fieldKey: 'cpq_approval_chains_count',
        fullPath: 'quote_and_pricing.cpq_approval_chains_count',
        valueType: 'number',
        description: 'Number of Advanced Approvals chains.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'quote_and_pricing',
        issueKey: 'large_quote_performance',
        discoveryLabel: 'Typical Quote Line Count',
        questionKey: 'quote_and_pricing',
        fieldKey: 'typical_quote_line_count',
        fullPath: 'quote_and_pricing.typical_quote_line_count',
        valueType: 'number',
        description: 'Average number of line items on a standard customer quote.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'quote_and_pricing',
        issueKey: 'large_quote_performance',
        discoveryLabel: 'Max Quote Line Count',
        questionKey: 'quote_and_pricing',
        fieldKey: 'max_quote_line_count',
        fullPath: 'quote_and_pricing.max_quote_line_count',
        valueType: 'number',
        description: 'Maximum line items observed on edge-case quotes.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'quote_and_pricing',
        issueKey: 'pricing_complexity_and_maintainability',
        discoveryLabel: 'Price Rules Count',
        questionKey: 'quote_and_pricing',
        fieldKey: 'price_rule_count',
        fullPath: 'quote_and_pricing.price_rule_count',
        valueType: 'number',
        description: 'Number of active pricing rules across products.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'quote_and_pricing',
        issueKey: 'pricing_complexity_and_maintainability',
        discoveryLabel: 'Price Books Count',
        questionKey: 'quote_and_pricing',
        fieldKey: 'price_book_count',
        fullPath: 'quote_and_pricing.price_book_count',
        valueType: 'number',
        description: 'Number of custom price books in use.',
        mappingVersion: '1.0',
    },
    // --- Billing ---
    {
        areaKey: 'billing',
        issueKey: 'quote_to_invoice_handoff',
        discoveryLabel: 'Monthly Invoice Volume',
        questionKey: 'billing',
        fieldKey: 'monthly_invoice_volume',
        fullPath: 'billing.monthly_invoice_volume',
        valueType: 'number',
        description: 'Average invoices generated per billing cycle.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'billing',
        issueKey: 'quote_to_invoice_handoff',
        discoveryLabel: 'Non Standard Billing Terms',
        questionKey: 'billing',
        fieldKey: 'non_standard_billing_terms',
        fullPath: 'billing.non_standard_billing_terms',
        valueType: 'boolean',
        description: 'Whether custom payment milestones or non-standard terms apply.',
        mappingVersion: '1.0',
    },
    // --- Data & Org Health ---
    {
        areaKey: 'data_and_org_health',
        issueKey: 'automation_consolidation',
        discoveryLabel: 'Active Workflows Count',
        questionKey: 'data_and_org_health',
        fieldKey: 'active_workflows_count',
        fullPath: 'data_and_org_health.active_workflows_count',
        valueType: 'number',
        description: 'Legacy workflow rules to migrate to Salesforce Flow.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'data_and_org_health',
        issueKey: 'automation_consolidation',
        discoveryLabel: 'Active Process Builders Count',
        questionKey: 'data_and_org_health',
        fieldKey: 'active_process_builders_count',
        fullPath: 'data_and_org_health.active_process_builders_count',
        valueType: 'number',
        description: 'Process Builders to migrate to Salesforce Flow.',
        mappingVersion: '1.0',
    },
    {
        areaKey: 'data_and_org_health',
        issueKey: 'automation_consolidation',
        discoveryLabel: 'Active Record Triggered Flows Count',
        questionKey: 'data_and_org_health',
        fieldKey: 'active_record_triggered_flows_count',
        fullPath: 'data_and_org_health.active_record_triggered_flows_count',
        valueType: 'number',
        description: 'Existing record-triggered flows being re-architected.',
        mappingVersion: '1.0',
    },
];
/** Lookup map by full questionKey.fieldKey */
export const SALESFORCE_BPR_DRIVER_BY_PATH = Object.fromEntries(SALESFORCE_BPR_DRIVER_MAP.map((m) => [m.fullPath, m]));
/** Lookup mappings by area and issue key */
export function getDriversForIssue(areaKey, issueKey) {
    return SALESFORCE_BPR_DRIVER_MAP.filter((m) => m.areaKey === areaKey && m.issueKey === issueKey);
}
//# sourceMappingURL=salesforce-bpr-driver-map.js.map